import { baseAPICreator } from './apiCreator';
import {
  Preference,
  PreferenceListResponse,
  CreatePreferenceRequest,
  UpdatePreferenceRequest,
  CampaignPreferenceResponseDto,
  CampaignPreferenceCreateRequestDto,
  CampaignPreferenceUpdateRequestDto,
  PreferenceListQueryParams,
  PreferenceData,
} from 'src/types/preference';
import { RISK_COLUMNS } from 'src/modals/PreferencesModal/RiskColumnSelector';

const preferenceApi = baseAPICreator('/campaign/preferences');

const collapseRiskColumnWhitespace = (s: string) =>
  s.replace(/\s+/g, ' ').trim();

const canonicalRiskColumnId = (input: string): string => {
  const n = collapseRiskColumnWhitespace(input);
  const match = RISK_COLUMNS.find((c) => collapseRiskColumnWhitespace(c) === n);
  return match ?? input;
};

export const normalizeGenreRiskOverrides = (
  raw: unknown
): PreferenceData['GENRE_RISK_OVERRIDES'] => {
  if (!Array.isArray(raw)) return [];
  const validMax = new Set(['Low', 'Medium', 'High']);
  return raw.map((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return { pattern: '', columns: [], max_risk: 'High' };
    }
    const o = item as Record<string, unknown>;
    const pattern = typeof o.pattern === 'string' ? o.pattern : '';
    const rawMax =
      typeof o.max_risk === 'string' && validMax.has(o.max_risk)
        ? o.max_risk
        : 'High';
    const cols = Array.isArray(o.columns)
      ? o.columns.filter((c): c is string => typeof c === 'string')
      : [];
    return {
      pattern,
      columns: cols.map(canonicalRiskColumnId),
      max_risk: rawMax,
    };
  });
};

/** Backend / JSON may send `{ pattern, label }[]`; UI expects category name strings. */
export const normalizeIabTopExclusionRules = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const s = item.trim();
      if (s) out.push(s);
    } else if (item && typeof item === 'object' && !Array.isArray(item)) {
      const o = item as Record<string, unknown>;
      const label = typeof o.label === 'string' ? o.label.trim() : '';
      const pattern = typeof o.pattern === 'string' ? o.pattern.trim() : '';
      const s = label || pattern;
      if (s) out.push(s);
    }
  }
  return Array.from(new Set(out));
};

// Convert API response to internal format
const mapApiResponseToPreference = (
  dto: CampaignPreferenceResponseDto
): Preference => {
  const preferenceData: PreferenceData = {
    RISK_ORDER: dto.preference_json?.RISK_ORDER || ['Low', 'Medium', 'High'],
    preferences: dto.preferences || {},
    GENRE_EXCLUSIONS: dto.genre_exclusions || [],
    GENRE_EXCLUSION_BYPASS_RULES: dto.genre_exclusion_bypass_rules || [],
    SHOW_EXCLUSIONS: dto.show_exclusions || [],
    SHOW_INCLUSIONS_FORCE_ALIGN0: dto.show_inclusions_force_align || [],
    IAB_TOP_EXCLUSION_RULES: normalizeIabTopExclusionRules(
      dto.iab_top_exclusion_rules
    ),
    GENRE_RISK_OVERRIDES: normalizeGenreRiskOverrides(dto.genre_risk_overrides),
    LANGUAGE_POLICY: Array.isArray(dto.language_policy)
      ? dto.language_policy
      : typeof dto.language_policy === 'object' && dto.language_policy !== null
      ? (dto.language_policy as any).english_only
        ? ['en']
        : []
      : [],
  };

  return {
    id: dto.id,
    name: dto.title,
    data: preferenceData,
    organizationId: '',
    createdBy: '',
    createdAt: '',
    updatedAt: '',
    ...(dto.date_column_override !== undefined
      ? { date_column_override: dto.date_column_override }
      : {}),
  };
};

// Convert internal format to API request
const mapPreferenceToApiRequest = (
  request: CreatePreferenceRequest | UpdatePreferenceRequest
): CampaignPreferenceCreateRequestDto | CampaignPreferenceUpdateRequestDto => {
  const data = request.data || ({} as PreferenceData);

  return {
    title: request.name || '',
    preference_json: {
      RISK_ORDER: data.RISK_ORDER || ['Low', 'Medium', 'High'],
    },
    preferences: data.preferences || {},
    genre_exclusions: data.GENRE_EXCLUSIONS || [],
    show_exclusions: data.SHOW_EXCLUSIONS || [],
    show_inclusions_force_align: data.SHOW_INCLUSIONS_FORCE_ALIGN0 || [],
    genre_exclusion_bypass_rules: data.GENRE_EXCLUSION_BYPASS_RULES || [],
    iab_top_exclusion_rules: data.IAB_TOP_EXCLUSION_RULES || [],
    genre_risk_overrides: data.GENRE_RISK_OVERRIDES || [],
    language_policy: Array.isArray(data.LANGUAGE_POLICY)
      ? data.LANGUAGE_POLICY
      : [],
    ...(request.date_column_override !== undefined
      ? { date_column_override: request.date_column_override }
      : {}),
  };
};

export const getPreferenceList = async (
  params?: PreferenceListQueryParams
): Promise<PreferenceListResponse> => {
  const queryParams = {
    page: params?.page ?? 0,
    pageSize: params?.pageSize ?? 10,
    sortBy: params?.sortBy ?? 'createdAt',
    sortOrder: params?.sortOrder ?? 'DESC',
  };

  const queryString = new URLSearchParams(
    Object.entries(queryParams).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  const { data } = await preferenceApi.get(`?${queryString}`);

  return {
    count: data.count || 0,
    preferences: (data.preferences || []).map(mapApiResponseToPreference),
  };
};

export const getPreference = async (id: string): Promise<Preference> => {
  const { data } = await preferenceApi.get(`/${id}`);
  return mapApiResponseToPreference(data);
};

export const createPreference = async (
  body: CreatePreferenceRequest
): Promise<Preference> => {
  const apiBody = mapPreferenceToApiRequest(
    body
  ) as CampaignPreferenceCreateRequestDto;
  const { data } = await preferenceApi.post('/', apiBody);
  return mapApiResponseToPreference(data);
};

export const updatePreference = async (
  id: string,
  body: UpdatePreferenceRequest
): Promise<Preference> => {
  const apiBody = mapPreferenceToApiRequest(
    body
  ) as CampaignPreferenceUpdateRequestDto;
  const { data } = await preferenceApi.put(`/${id}`, apiBody);
  return mapApiResponseToPreference(data);
};

export const deletePreference = async (id: string): Promise<void> => {
  await preferenceApi.delete(`/${id}`);
};
